import * as React from 'react';

export type PanelProps<T = object> = T & {
  closePanel?: () => void;
  /** Close all panels and return to the root panel */
  closeToRoot?: () => void;
  openPanel?: <P extends object>(panel: Panel<P>) => void;
};

export type Panel<T = object> = {
  props?: T;
  renderPanel: (props: PanelProps<T>) => React.ReactElement | null;
  title?: string;
};

export type PanelStackProps<T = object> = {
  initialPanel: Panel<T>;
  renderActivePanelOnly?: boolean;
};

type PanelWithId<T = object> = Panel<T> & {
  __id: number;
};

let nextPanelId = 0;

const PanelRenderer: React.FC<{
  panel: Panel<any>;
  closePanel?: () => void;
  closeToRoot: () => void;
  openPanel: <P extends object>(panel: Panel<P>) => void;
}> = ({ panel, ...props }) => {
  const Component = panel.renderPanel;
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Component {...panel.props} {...props} />;
};

export const PanelStack = <T extends object = object>({
  initialPanel,
  renderActivePanelOnly = false,
}: PanelStackProps<T>) => {
  const [panelStack, setPanelStack] = React.useState<Array<PanelWithId<any>>>(
    () => {
      const id = nextPanelId;
      nextPanelId += 1;
      return [{ ...initialPanel, __id: id }];
    }
  );

  const openPanel = React.useCallback(<P extends object>(panel: Panel<P>) => {
    setPanelStack((prev) => {
      const id = nextPanelId;
      nextPanelId += 1;
      return [...prev, { ...panel, __id: id }];
    });
  }, []);

  const closePanel = React.useCallback(() => {
    setPanelStack((prev) => {
      if (prev.length > 1) {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);

  const closeToRoot = React.useCallback(() => {
    setPanelStack((prev) => {
      if (prev.length > 1) {
        return prev.slice(0, 1);
      }
      return prev;
    });
  }, []);

  const currentPanel = panelStack[panelStack.length - 1];

  if (renderActivePanelOnly) {
    return (
      <PanelRenderer
        key={currentPanel.__id}
        closePanel={panelStack.length > 1 ? closePanel : undefined}
        closeToRoot={closeToRoot}
        openPanel={openPanel}
        panel={currentPanel}
      />
    );
  }

  return (
    <>
      {panelStack.map((panel, index) => (
        <div
          key={panel.__id}
          style={{
            display: index === panelStack.length - 1 ? 'block' : 'none',
          }}
        >
          <PanelRenderer
            key={panel.__id}
            closePanel={closePanel}
            closeToRoot={closeToRoot}
            openPanel={openPanel}
            panel={panel}
          />
        </div>
      ))}
    </>
  );
};
